/*----------------------------------
- PUBLIC RADAR QUERY
----------------------------------*/

/**
 * Bridges the page's URL filters and the shared filter rail.
 *
 * The rail speaks `RadarQuery`, the page speaks the encoded radar filters its URL carries. The
 * rail never applies anything on this page (ADR-0035), so these two helpers only have to build
 * the query it renders and the product URL a filter click hands off to.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import type { RadarQuery, RadarRuleCondition } from '@uniquedomains/components/radar/rules/types';
import { BROWSE_DOMAIN_PANEL_QUERY_KEY } from '@uniquedomains/components/radar/urlCodec';
import { encodeRadarFiltersUrl } from '@/common/libs/filters';
import type { TInputRadar } from '@/common/types/domains/radar';
import { toSharedRadarRules } from '../askAiSearchFilters';

/*----------------------------------
- PUBLIC API
----------------------------------*/

/**
 * The query the rail renders for the search currently on screen.
 *
 * `toSharedRadarRules` is the page's existing bridge between the website's own rule tree, whose
 * `filterId` is optional, and the shared condition type the rail reads. It falls back to the
 * backend `field`, so every rule reaches a control and nothing needs dropping here.
 */
export const buildPublicRadarQuery = (filters: Partial<TInputRadar>): RadarQuery => ({
    mode: 'investor',
    view: 'domains',
    rules: toSharedRadarRules(filters.rules),
});

/**
 * The product URL a filter click hands off to, carrying the rule the visitor just set.
 *
 * Nested OR groups flatten to one AND group: the rail only ever produces a flat condition list,
 * so re-encoding it as a single AND group is what the visitor actually asked for, and the product
 * reads it back into the same rail.
 */
export const buildProductFilterUrl = (
    filters: Partial<TInputRadar>,
    nextRules: readonly RadarRuleCondition[],
): string =>
    encodeRadarFiltersUrl(
        { ...filters, rules: { op: 'AND', rules: [...nextRules] } },
        { surface: 'product' },
    );

/**
 * The product URL a row click hands off to: the visitor's own search, plus the name they opened.
 *
 * The search stays whole, so closing the domain panel shows the results the visitor was reading.
 * It carries the name rather than a row id: the name is what the visitor clicked, and Browse
 * looks a name up on its own when the row is not among the results it loaded. A name that is no
 * longer listed leaves the plain list on screen, never an error.
 *
 * `absoluteProductSearchUrl` is the page's `productHref`, which is always absolute.
 */
export const buildProductDomainUrl = (absoluteProductSearchUrl: string, domain: string): string => {
    const url = new URL(absoluteProductSearchUrl);
    url.searchParams.set(BROWSE_DOMAIN_PANEL_QUERY_KEY, domain);
    return url.toString();
};
